import React, { useState } from "react";
import { Box, Collapse, Typography, Avatar, Skeleton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import TasksListTable from "./TasksListTable";
import {
  TASK_STATUS_COMPLETED_COLOR,
  TASK_STATUS_PENDING_COLOR,
  TASK_STATUS_IN_PROGRESS_COLOR,
  TASK_STATUS_CANCELLED_COLOR,
} from "../../utils/taskTemplateChipStyles";

// Vibrant person-avatar palette (shared standard across the task tables).
const PERSON_AVATAR_PALETTE = [
  "#6366f1", "#ec4899", "#f59e0b", "#10b981",
  "#3b82f6", "#8b5cf6", "#ef4444", "#14b8a6",
  "#f97316", "#06b6d4", "#84cc16", "#e11d48",
];

const personAvatarColor = (name) => {
  let hash = 0;
  const s = String(name || "");
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  return PERSON_AVATAR_PALETTE[Math.abs(hash) % PERSON_AVATAR_PALETTE.length];
};

// Status breakdown shown per group — counts are derived from the group's own rows.
const STATUS_BREAKDOWN = [
  { label: "Completed", color: TASK_STATUS_COMPLETED_COLOR, test: (r) => /complete|done|finished|closed/i.test(String(r.status || "")) },
  { label: "Pending", color: TASK_STATUS_PENDING_COLOR, test: (r) => /pending/i.test(String(r.status || "")) },
  { label: "In Progress", color: TASK_STATUS_IN_PROGRESS_COLOR, test: (r) => /progress/i.test(String(r.status || "")) },
  { label: "Cancelled", color: TASK_STATUS_CANCELLED_COLOR, test: (r) => /cancel/i.test(String(r.status || "")) },
];

const VARIANT_META = {
  leader: { subtitle: "Team lead's tasks", accent: "#16a34a" },
  member: { subtitle: "Assigned tasks", accent: null },
  unassigned: { subtitle: "Not assigned to anyone", accent: "#8b5cf6" },
};

const BRAND_GREEN = "#0E9046";

// Section heading above the team banner / assigned-tasks list — a little bigger, dull black.
const sectionHeadingSx = {
  fontSize: { xs: "1.05rem", sm: "1.12rem" },
  fontWeight: 700,
  color: (theme) => (theme.palette.mode === "dark" ? "#e2e8f0" : "#374151"),
  mb: 1.25,
};

// Status dot-counts derived from the given rows (md+ only; hidden on small screens).
// `rows` here is the STATS source (scope/search-filtered but NOT status-filtered)
// so the counts stay stable while the status filter pill changes the table below.
const StatusBreakdown = ({ rows }) => (
  <>
    <Box
      sx={{
        display: { xs: "none", md: "block" },
        alignSelf: "stretch",
        width: "1px",
        my: 0.25,
        bgcolor: "divider",
        flexShrink: 0,
      }}
    />
    <Box
      sx={{
        display: { xs: "none", md: "flex" },
        alignItems: "center",
        gap: { md: 1.75, lg: 2.75 },
        flexShrink: 0,
        pl: { md: 1.5 },
      }}
    >
      {STATUS_BREAKDOWN.map((s) => {
        const count = rows.filter((r) => s.test(r)).length;
        return (
          <Box
            key={s.label}
            sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 52 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: s.color, flexShrink: 0 }} />
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "text.primary" }}>
                {count}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", mt: 0.15 }}>
              {s.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  </>
);

// Placeholder shown while `grouped` hasn't arrived yet (matches MemberSection's
// collapsed header shape so the swap-in feels seamless once real data lands).
const GroupedSectionSkeleton = () => (
  <Box
    sx={{
      mb: 1.25,
      borderRadius: 2,
      overflow: "hidden",
      bgcolor: "background.paper",
      border: (theme) => `1px solid ${theme.palette.divider}`,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: { xs: 1, sm: 1.5 },
        px: { xs: 1.5, sm: 2 },
        py: 1.35,
      }}
    >
      <Skeleton variant="circular" width={24} height={24} sx={{ flexShrink: 0 }} />
      <Skeleton variant="circular" width={40} height={40} sx={{ flexShrink: 0 }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Skeleton width="35%" height={20} />
        <Skeleton width="50%" height={16} />
      </Box>
    </Box>
  </Box>
);

const MemberSection = ({
  title,
  variant = "member",
  rows,
  statsRows,
  defaultOpen = false,
  fieldOptions,
  loading,
  onView,
  onEdit,
  onDelete,
  onBulkDelete,
  onStatusChange,
  onPriorityChange,
  onTypeChange,
  canView,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  if (!rows?.length) return null;

  const meta = VARIANT_META[variant] || VARIANT_META.member;
  const accent = meta.accent || personAvatarColor(title);

  return (
    <Box
      sx={{
        mb: 1.25,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "background.paper",
        border: (theme) =>
          open ? `1px solid ${alpha(accent, 0.5)}` : `1px solid ${theme.palette.divider}`,
        boxShadow: (theme) =>
          theme.palette.mode === "dark" ? "none" : "0 1px 2px rgba(15,23,42,0.05)",
        transition: (theme) =>
          theme.transitions.create(["border-color", "box-shadow"], {
            duration: theme.transitions.duration.shortest,
          }),
      }}
    >
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, sm: 1.5 },
          px: { xs: 1.5, sm: 2 },
          py: 1.35,
          cursor: "pointer",
          bgcolor: (theme) =>
            open ? alpha(accent, theme.palette.mode === "dark" ? 0.12 : 0.05) : "transparent",
          transition: (theme) =>
            theme.transitions.create(["background-color"], {
              duration: theme.transitions.duration.shortest,
            }),
          "&:hover": {
            bgcolor: (theme) => alpha(accent, theme.palette.mode === "dark" ? 0.14 : 0.06),
          },
        }}
      >
        <ExpandMoreIcon
          sx={{
            flexShrink: 0,
            fontSize: "1.35rem",
            color: open ? accent : "text.secondary",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        />
        <Avatar
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            bgcolor: alpha(accent, 0.16),
            color: accent,
          }}
        >
          <PeopleAltRoundedIcon sx={{ fontSize: 22 }} />
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                fontWeight: 700,
                fontSize: { xs: "0.98rem", sm: "1.02rem" },
                color: "text.primary",
              }}
            >
              {title}
            </Typography>
            <Typography
              component="span"
              sx={{ flexShrink: 0, fontSize: "0.86rem", fontWeight: 500, color: "text.secondary" }}
            >
              ({rows.length})
            </Typography>
          </Box>
          <Typography
            noWrap
            sx={{ fontSize: "0.8rem", color: "text.secondary", lineHeight: 1.4, mt: 0.1 }}
          >
            {meta.subtitle}
          </Typography>
        </Box>

        <StatusBreakdown rows={statsRows} />
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            px: { xs: 1, sm: 1.5 },
            pt: 1.25,
            pb: 1.5,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <TasksListTable
            rows={rows}
            fieldOptions={fieldOptions}
            loading={loading}
            onView={onView}
            onEdit={onEdit}
            onDelete={onDelete}
            onBulkDelete={onBulkDelete}
            onStatusChange={onStatusChange}
            onPriorityChange={onPriorityChange}
            onTypeChange={onTypeChange}
            canView={canView}
            canEdit
            hidePagination
            showBulkActionsBar
            bulkBarInset
            fillViewport={false}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

const TasksGroupedListTable = ({
  grouped,
  statsGrouped,
  teamName,
  fieldOptions,
  loading,
  onView,
  onEdit,
  onDelete,
  onBulkDelete,
  onStatusChange,
  onPriorityChange,
  onTypeChange,
  canView,
}) => {
  if (!grouped) {
    if (!loading) return null;
    return (
      <Box sx={{ flex: "0 0 auto", minHeight: 0 }}>
        <Typography sx={{ ...sectionHeadingSx, mb: 1.75 }}>
          <Skeleton width={140} />
        </Typography>
        <GroupedSectionSkeleton />
        <GroupedSectionSkeleton />
        <GroupedSectionSkeleton />
      </Box>
    );
  }

  // Stats source mirrors `grouped` but skips the active status filter, so the
  // per-section breakdown stays fixed while the status pill only changes the
  // table rows below. Falls back to `grouped` if a caller doesn't supply it.
  const statsSource = statsGrouped || grouped;
  const statsByMemberId = new Map(
    (statsSource.members || []).map((bucket) => [bucket.member?.id, bucket.tasks || []])
  );

  const leaderName = grouped.team?.leader?.name || "Team leader";

  const allTasks = [
    ...(grouped.leaderTasks || []),
    ...(grouped.members || []).flatMap((b) => b.tasks || []),
    ...(grouped.unassignedTasks || []),
  ];
  const groupCount =
    ((grouped.leaderTasks || []).length ? 1 : 0) +
    (grouped.members || []).filter((b) => (b.tasks || []).length).length +
    ((grouped.unassignedTasks || []).length ? 1 : 0);

  return (
    <Box sx={{ flex: "0 0 auto", minHeight: 0 }}>
      {teamName && <Typography sx={{ ...sectionHeadingSx, mb: 1.75 }}>Selected Team</Typography>}
      {teamName && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1.5, sm: 1.75 },
            pl: { xs: 1.75, sm: 2.25 },
            pr: { xs: 2, sm: 2.5 },
            py: { xs: 1.5, sm: 1.75 },
            mb: 2,
            borderRadius: 2,
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#fbfcfb",
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderLeft: `4px solid ${BRAND_GREEN}`,
          }}
        >
          <Avatar
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              bgcolor: alpha(BRAND_GREEN, 0.14),
              color: BRAND_GREEN,
            }}
          >
            <PeopleAltRoundedIcon sx={{ fontSize: 24 }} />
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              noWrap
              sx={{
                fontSize: { xs: "1.12rem", sm: "1.2rem" },
                fontWeight: 700,
                lineHeight: 1.25,
                color: "text.primary",
              }}
            >
              {teamName}
            </Typography>
            <Typography
              noWrap
              sx={{ fontSize: "0.8rem", color: "text.secondary", lineHeight: 1.4, mt: 0.1 }}
            >
              {allTasks.length} {allTasks.length === 1 ? "task" : "tasks"} · {groupCount}{" "}
              {groupCount === 1 ? "member" : "members"}
            </Typography>
          </Box>
        </Box>
      )}
      <Typography sx={{ ...sectionHeadingSx, mt: teamName ? 4 : 0 }}>Assigned Tasks</Typography>
      <MemberSection
        title={leaderName}
        variant="leader"
        rows={grouped.leaderTasks || []}
        statsRows={statsSource.leaderTasks || []}
        fieldOptions={fieldOptions}
        loading={loading}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete}
        onBulkDelete={onBulkDelete}
        onStatusChange={onStatusChange}
        onPriorityChange={onPriorityChange}
        onTypeChange={onTypeChange}
        canView={canView}
      />
      {(grouped.members || []).map((bucket) => (
        <MemberSection
          key={bucket.member?.id}
          title={bucket.member?.name || "Member"}
          variant="member"
          rows={bucket.tasks || []}
          statsRows={statsByMemberId.get(bucket.member?.id) || []}
          fieldOptions={fieldOptions}
          loading={loading}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          onBulkDelete={onBulkDelete}
          onStatusChange={onStatusChange}
          onPriorityChange={onPriorityChange}
          onTypeChange={onTypeChange}
          canView={canView}
        />
      ))}
      <MemberSection
        title="Unassigned"
        variant="unassigned"
        rows={grouped.unassignedTasks || []}
        statsRows={statsSource.unassignedTasks || []}
        fieldOptions={fieldOptions}
        loading={loading}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete}
        onBulkDelete={onBulkDelete}
        onStatusChange={onStatusChange}
        onPriorityChange={onPriorityChange}
        onTypeChange={onTypeChange}
        canView={canView}
      />
    </Box>
  );
};

export default TasksGroupedListTable;
